/**
 * TCX-3 (K97) — app → Training Companion token handoff (content-script bridge).
 *
 * PRODUCTION DESIGN (no extension ID / no `externally_connectable` required):
 * The Companion extension injects `contentScript.js` ONLY on OptiBay app
 * origins (manifest `content_scripts.matches`). That content script listens for
 * a same-window `window.postMessage` of type
 * `OPTIBAY_TRAINING_COMPANION_TOKEN`, validates it (`event.source === window`,
 * type + payload shape), forwards it to the extension's own background service
 * worker via same-extension `chrome.runtime.sendMessage` (which needs NO
 * extension id), and ACKs back with a NON-SECRET
 * `OPTIBAY_TRAINING_COMPANION_ACK` (sessionId only). Because the bridge runs
 * inside the installed extension, the app never needs the published Web Store
 * extension id to hand off the token — it only needs the extension installed.
 *
 * This helper performs the app side of that contract:
 *   - posts the token message with a strict `targetOrigin` (current origin),
 *   - waits for the matching ACK with a timeout,
 *   - resolves `{ connected }` so the caller can show an honest fallback when
 *     the Companion is not installed/listening.
 *
 * SECURITY:
 *   - The plaintext token is posted ONLY to the current origin and is never
 *     logged, persisted, or returned to the caller.
 *   - The ACK carries no secret (sessionId only); we match it by sessionId.
 *   - No-op (resolves `{ connected: false }`) outside a browser window (SSR).
 */

export const COMPANION_TOKEN_MESSAGE_TYPE = 'OPTIBAY_TRAINING_COMPANION_TOKEN';
export const COMPANION_ACK_MESSAGE_TYPE = 'OPTIBAY_TRAINING_COMPANION_ACK';
export const COMPANION_HANDOFF_DEFAULT_TIMEOUT_MS = 2000;

/**
 * K108-P launch mode:
 *  - 'companion_opens': the Companion opens + owns the provider tab (monitored).
 *  - 'app_opens': the app already opened the tab (legacy / unmonitored fallback).
 */
export type CompanionLaunchMode = 'companion_opens' | 'app_opens';

export interface CompanionTokenHandoffInput {
  sessionId: string;
  /** One-time plaintext Companion monitoring token. Never logged/persisted. */
  token: string;
  expiresAt?: string | null;
  openedUrlSnapshot?: string | null;
  /** K108-P: the approved provider URL the Companion should open + monitor. */
  providerUrl?: string | null;
  /** K108-P: approved-domain allowlist for this launch (defense-in-depth). */
  approvedDomains?: string[] | null;
  /** K108-P: who opens the provider tab. Default 'app_opens' (back-compat). */
  launchMode?: CompanionLaunchMode;
  /** K108-P: expected training duration (seconds) for confidence scoring. */
  expectedDurationSec?: number | null;
  /**
   * K108-Z: RESUME/REHYDRATE handoff — the Companion already owns the provider tab
   * (a monitor restored after a SW restart); this hands back ONLY a fresh token so
   * the Companion reconnects. When true the Companion must NOT open a new tab or
   * start a new observation — just store the token and resume the restored monitor.
   */
  rehydrate?: boolean;
  /**
   * K108-M: the app's OWN Supabase project URL (NON-SECRET public endpoint host,
   * e.g. https://<ref>.supabase.co). Handed to the Companion so it POSTs evidence
   * to the SAME backend the app uses (preview/dev/prod differ) instead of
   * requiring the user to type it into the popup. Defaults to
   * `VITE_SUPABASE_URL`. Not the anon key — no secret is sent.
   */
  supabaseUrl?: string | null;
}

/** Non-secret Supabase endpoint host the app is running against (env-driven). */
function appSupabaseUrl(): string | null {
  try {
    const env = (import.meta as unknown as { env?: Record<string, string> }).env;
    const url = env?.VITE_SUPABASE_URL;
    return typeof url === 'string' && url.startsWith('https://') ? url : null;
  } catch {
    return null;
  }
}

export interface CompanionTokenHandoffResult {
  /** True only when the Companion content script ACK'd this exact sessionId. */
  connected: boolean;
  /**
   * K108-P: true when the Companion reported it OPENED + owns the provider tab
   * (companion_opens path). When false, the caller must open the tab itself.
   */
  launched: boolean;
}

/**
 * Emit the minted Companion token to the Training Companion via the
 * content-script bridge and await its ACK (bounded by `timeoutMs`).
 */
export function emitCompanionTokenHandoff(
  input: CompanionTokenHandoffInput,
  timeoutMs: number = COMPANION_HANDOFF_DEFAULT_TIMEOUT_MS,
): Promise<CompanionTokenHandoffResult> {
  // SSR / non-browser guard — nothing to hand off to.
  if (typeof window === 'undefined' || typeof window.postMessage !== 'function') {
    return Promise.resolve({ connected: false, launched: false });
  }

  // A valid sessionId + token are required; never post an empty/partial payload.
  if (!input.sessionId || !input.token) {
    return Promise.resolve({ connected: false, launched: false });
  }

  const origin = window.location.origin;

  return new Promise<CompanionTokenHandoffResult>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      if (timer !== undefined) clearTimeout(timer);
    };

    const finish = (connected: boolean, launched: boolean) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ connected, launched });
    };

    function onMessage(event: MessageEvent) {
      // Only accept the ACK from THIS window on the SAME origin.
      if (event.source !== window) return;
      if (event.origin !== origin) return;
      const data = event.data as
        | { type?: unknown; sessionId?: unknown; launched?: unknown }
        | null;
      if (!data || typeof data !== 'object') return;
      if (data.type !== COMPANION_ACK_MESSAGE_TYPE) return;
      if (data.sessionId !== input.sessionId) return;
      // K108-P: the ACK reports whether the Companion opened the provider tab.
      finish(true, data.launched === true);
    }

    window.addEventListener('message', onMessage);
    timer = setTimeout(() => finish(false, false), timeoutMs);

    // Post the token to the content-script bridge. Strict targetOrigin so the
    // message is only delivered to same-origin listeners (the Companion content
    // script, which additionally checks `event.source === window`).
    window.postMessage(
      {
        type: COMPANION_TOKEN_MESSAGE_TYPE,
        payload: {
          sessionId: input.sessionId,
          token: input.token,
          expiresAt: input.expiresAt ?? null,
          openedUrlSnapshot: input.openedUrlSnapshot ?? null,
          // K108-M: non-secret backend endpoint so the Companion targets the
          // same project the app runs against (no manual popup entry needed).
          supabaseUrl: input.supabaseUrl ?? appSupabaseUrl(),
          // K108-P: provider-tab monitoring — the approved URL the Companion
          // should open+own, the approved-domain allowlist, and who opens it.
          providerUrl: input.providerUrl ?? null,
          approvedDomains: input.approvedDomains ?? null,
          launchMode: input.launchMode ?? 'app_opens',
          expectedDurationSec: input.expectedDurationSec ?? null,
          // K108-Z: rehydrate-only handoff (no new tab / observation).
          rehydrate: input.rehydrate === true,
        },
      },
      origin,
    );
  });
}
